import React, { useState } from "react";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTranslation } from "react-i18next";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import {
  selectRepeatPaths,
  selectSelectedRoomCleaningZones,
  selectSelectedRoomGates,
  selectSelectedRoomLandmarks,
  selectSelectedRoomRedZones,
  selectSelectedRoomRuleZones,
  selectSelectedRoomWaypoints,
  selectWaypointsData,
} from "../../../store/room/room-selectors";
import {
  selectHiddenGateIds,
  selectHiddenLandmarkIds,
  selectHiddenZoneIds,
  selectZonePlannerModeState,
} from "../../../store/room/zone-planner/zone-planner-selectors";
import ZonePlannerModeState from "../../../enums/robot/zone-planner/zone-planner-mode-state";
import { LandmarkType } from "../../../interfaces/robot/rooms/landmark";
import { compareZoneNames } from "../../../utils/map/zone-helper";
import ReadOnlyProtectedIconButton from "../../../components/Inputs/ReadOnlyProtectedIconButton";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import { MapHistoryAction, MapHistoryType } from "../../../interfaces/robot/zone-planner/map-history";
import { getRobotApiConfig } from "../../../interfaces/robot/robot-apis/robot-api-config";
import ApiEndpointNames from "../../../enums/robot/configuration/api/api-endpoint-names";
import ApiProtocol from "../../../enums/robot/configuration/api/api-protocol";
import { selectRobotApiConfigs } from "../../../store/robot-configurations/robot-configurations-selectors";

const LayersList: React.FC<{
  isTransitionRoom?: boolean;
  onHoverZoneIdChange: (zoneId: string | null) => void;
  onHoverGateIdChange: (gateId: string | null) => void;
}> = ({ isTransitionRoom, onHoverZoneIdChange, onHoverGateIdChange }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const {
    toggleWayPointVisibility,
    toggleAllZonesHidden,
    toggleAllGatesHidden,
    toggleAllLandmarksHidden,
    toggleZoneIdHidden,
    toggleGateIdHidden,
    toggleLandmarkIdHidden,
    deleteCleaningZoneById,
    deleteRedZoneById,
    deleteRuleZoneById,
    deleteLandmarkById,
    deleteGateById,
    addUndoableAction,
  } = useSharedActions();

  const zonePlannerModeState = useSharedSelector((state) => selectZonePlannerModeState(state));
  const cleaningZones = useSharedSelector((state) => selectSelectedRoomCleaningZones(state));
  const repeatPaths = useSharedSelector((state) => selectRepeatPaths(state));
  const redZones = useSharedSelector((state) => selectSelectedRoomRedZones(state));
  const ruleZones = useSharedSelector((state) => selectSelectedRoomRuleZones(state));
  const hiddenZoneIds = useSharedSelector((state) => selectHiddenZoneIds(state));
  const gates = useSharedSelector((state) => selectSelectedRoomGates(state));
  const hiddenGateIds = useSharedSelector((state) => selectHiddenGateIds(state));
  const landmarks = useSharedSelector((state) => selectSelectedRoomLandmarks(state));
  const hiddenLandmarkIds = useSharedSelector((state) => selectHiddenLandmarkIds(state));
  const wayPoints = useSharedSelector((state) => selectSelectedRoomWaypoints(state));
  const wayPointsData = useSharedSelector((state) => selectWaypointsData(state));
  const robotApiConfigs = useSharedSelector((state) => selectRobotApiConfigs(state));

  const toggleWayPointVisibilityHandler = () => {
    dispatch(toggleWayPointVisibility());
  };

  const toggleAllZonesVisibilityHandler = (items: string[], isSelected: boolean) => {
    dispatch(toggleAllZonesHidden({ ids: items, showAll: !isSelected }));
  };

  const toggleAllGatesVisibilityHandler = (items: string[], isSelected: boolean) => {
    dispatch(toggleAllGatesHidden({ ids: items, showAll: !isSelected }));
  };

  const toggleAllLandmarksVisibilityHandler = (items: string[], isSelected: boolean) => {
    dispatch(toggleAllLandmarksHidden({ ids: items, showAll: !isSelected }));
  };

  const toggleSingleZoneVisibilityHandler = (id: string) => {
    dispatch(toggleZoneIdHidden(id));
  };

  const toggleSingleGateVisibilityHandler = (id: string) => {
    dispatch(toggleGateIdHidden(id));
  };

  const toggleSingleLandmarkVisibilityHandler = (id: string) => {
    dispatch(toggleLandmarkIdHidden(id));
  };

  const deleteSingleCleaningZoneHandler = (id: string) => {
    const cleaningZone = cleaningZones.find(cz => cz.id === id);
    const repeatPath = repeatPaths.find(trp => trp.zoneId === id);
    if (cleaningZone != null){
      dispatch(addUndoableAction({
        action: MapHistoryAction.Deleted,
        type: MapHistoryType.CleaningZone,
        changedData: cleaningZone,
        additionalChangedData: repeatPath,
      }));
    }
    dispatch(deleteCleaningZoneById(id));
  };

  const deleteSingleRedZoneHandler = (id: string) => {
    const redZone = redZones.find(rz => rz.id === id)
    if(redZone != null){
      dispatch(addUndoableAction({
        action: MapHistoryAction.Deleted,
        type: MapHistoryType.RedZone,
        changedData: redZone,
      }));
    }
    dispatch(deleteRedZoneById(id));
  };

  const deleteSingleRuleZoneHandler = (id: string) => {
    dispatch(deleteRuleZoneById(id));
  };

  const deleteSingleGateHandler = (id: string) => {
    dispatch(deleteGateById(id));
  };

  const deleteSingleLandmarkHandler = (id: string) => {
    const landmark = landmarks.find(lm => lm.id === id);
    if (landmark != null){
      dispatch(addUndoableAction({
        action: MapHistoryAction.Deleted,
        type: MapHistoryType.Landmark,
        changedData: landmark,
      }));
    }
    dispatch(deleteLandmarkById(id));
  };

  const isRedZoneReadOnly = (zoneId: string) => {
    return zonePlannerModeState === ZonePlannerModeState.Default
      ? gates.some(g => g.redzone === zoneId)
      : zonePlannerModeState === ZonePlannerModeState.MapRules
        ? !gates.some(g => g.redzone === zoneId)
        : false;
  };

  return (
    <Card
      data-testid="layers-list"
      sx={(theme) => ({
        position: "absolute",
        width: "auto",
        minWidth: "280px",
        maxWidth: "300px",
        left: theme.spacing(1),
        top: { xs: theme.spacing(8), lg: theme.spacing(1) },
        zIndex: 1,
        overflowY: "auto",
        maxHeight: `calc(100% - ${theme.spacing(2)})`,
        padding: theme.spacing(1),
      })}
    >
      {zonePlannerModeState === ZonePlannerModeState.Default && isTransitionRoom !== true && (
        <LayerSection
          header={t("cleaning-zones-plural")}
          visibilityTooltip={t("tooltip-show-hide-zone") as string}
          deleteTooltip={t("action-delete-zone") as string}
          items={cleaningZones}
          hiddenIds={hiddenZoneIds}
          onHoverIdChange={onHoverZoneIdChange}
          onToggleAllHandler={toggleAllZonesVisibilityHandler}
          onToggleSingleHandler={toggleSingleZoneVisibilityHandler}
          onDeleteSingleHandler={deleteSingleCleaningZoneHandler}
        />
      )}

      <LayerSection
        header={t("red-zones-plural")}
        visibilityTooltip={t("tooltip-show-hide-zone") as string}
        deleteTooltip={t("action-delete-zone") as string}
        items={redZones.filter(
          (z) =>
            zonePlannerModeState === ZonePlannerModeState.EvacuationMode ||
            redZones.some((rz) => rz.id === z.id && !rz.evacuation),
        )}
        hiddenIds={hiddenZoneIds}
        onHoverIdChange={onHoverZoneIdChange}
        isReadOnly={isRedZoneReadOnly}
        onToggleAllHandler={toggleAllZonesVisibilityHandler}
        onToggleSingleHandler={toggleSingleZoneVisibilityHandler}
        onDeleteSingleHandler={deleteSingleRedZoneHandler}
      />

      {zonePlannerModeState === ZonePlannerModeState.MapRules && (
        <LayerSection
          header={t("rule-zones-plural")}
          visibilityTooltip={t("tooltip-show-hide-zone") as string}
          deleteTooltip={t("action-delete-zone") as string}
          items={ruleZones}
          hiddenIds={hiddenZoneIds}
          onHoverIdChange={onHoverZoneIdChange}
          onToggleAllHandler={toggleAllZonesVisibilityHandler}
          onToggleSingleHandler={toggleSingleZoneVisibilityHandler}
          onDeleteSingleHandler={deleteSingleRuleZoneHandler}
        />
      )}

      {getRobotApiConfig(ApiEndpointNames.GATES, robotApiConfigs) != null &&
        getRobotApiConfig(ApiEndpointNames.GATES, robotApiConfigs)?.apiProtocol !== ApiProtocol.Disabled &&
        (zonePlannerModeState === ZonePlannerModeState.Default ||
       
          zonePlannerModeState === ZonePlannerModeState.MapRules) && (
          <LayerSection
            header={t("gates-plural")}
            visibilityTooltip={t("tooltip-show-hide-gate") as string}
            deleteTooltip={t("action-delete-gate") as string}
            items={gates}
            hiddenIds={hiddenGateIds}
            isReadOnly={zonePlannerModeState === ZonePlannerModeState.Default}
            onHoverIdChange={onHoverGateIdChange}
            onToggleAllHandler={toggleAllGatesVisibilityHandler}
            onToggleSingleHandler={toggleSingleGateVisibilityHandler}
            onDeleteSingleHandler={deleteSingleGateHandler}
          />
        )}

      {(zonePlannerModeState === ZonePlannerModeState.EvacuationMode ||
        zonePlannerModeState === ZonePlannerModeState.Default) &&
        isTransitionRoom !== true && (
          <LayerSection
            header={zonePlannerModeState === ZonePlannerModeState.EvacuationMode ? t("evacuation-place-plural") : t("maintenance-point-plural")}
            visibilityTooltip={zonePlannerModeState === ZonePlannerModeState.EvacuationMode ? t("tooltip-show-hide-evacuation-place") : t("tooltip-show-hide-maintenance-point") as string}
            deleteTooltip={zonePlannerModeState === ZonePlannerModeState.EvacuationMode ? t("action-delete-evacuation-place") : t("action-delete-maintenance-point") as string}
            items={landmarks.filter(
              (lm) =>
                (lm.type === LandmarkType.Evacuation && zonePlannerModeState === ZonePlannerModeState.EvacuationMode) ||
                (lm.type === LandmarkType.Maintenance && zonePlannerModeState === ZonePlannerModeState.Default),
            )}
            hiddenIds={hiddenLandmarkIds}
            onToggleAllHandler={toggleAllLandmarksVisibilityHandler}
            onToggleSingleHandler={toggleSingleLandmarkVisibilityHandler}
            onDeleteSingleHandler={deleteSingleLandmarkHandler}
          />
        )}

      {(zonePlannerModeState === ZonePlannerModeState.Default ||
        zonePlannerModeState === ZonePlannerModeState.MapRules || 
        zonePlannerModeState === ZonePlannerModeState.EvacuationMode) && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography flexGrow={1} variant="subtitle2">
            {t("waypoints-plural")}
          </Typography>
          {wayPoints.nodes.length !== 0 && (
            <Tooltip title={t("tooltip-show-hide-waypoints") as string} placement="left">
              <Checkbox checked={wayPointsData.isVisible} onChange={toggleWayPointVisibilityHandler} />
            </Tooltip>
          )}
        </Stack>
      )}
    </Card>
  );
};

const LayerSection: React.FC<{
  header: string;
  items: { id: string; name: string | null }[];
  hiddenIds: string[];
  isReadOnly?: boolean | ((id: string) => boolean);
  visibilityTooltip: string;
  deleteTooltip: string;
  onHoverIdChange?: (zoneId: string | null) => void;
  onToggleAllHandler: (items: string[], isSelected: boolean) => void;
  onToggleSingleHandler: (id: string) => void;
  onDeleteSingleHandler: (id: string) => void;
}> = ({
  items,
  header,
  hiddenIds,
  isReadOnly = false,
  visibilityTooltip,
  deleteTooltip,
  onHoverIdChange,
  onToggleAllHandler,
  onToggleSingleHandler,
  onDeleteSingleHandler,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isSelected = items.filter((z) => hiddenIds.includes(z.id)).length === 0;

  const handleSelectedChange = () => {
    onToggleAllHandler(
      items.map((z) => z.id),
      isSelected
    );
  };

  return (
    <React.Fragment>
      <Stack spacing={1} direction="row" alignItems="center">
        <Typography flexGrow={1} variant="subtitle2">
          {header}
        </Typography>

        {items.length !== 0 && (
          <Tooltip title={visibilityTooltip} placement="left">
            <Checkbox checked={isSelected} color="primary" onChange={handleSelectedChange} />
          </Tooltip>
        )}
        <IconButton onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Stack>
      <Collapse in={isOpen}>
        {items
          .filter((z) => z.name != null)
          .sort((a, b) => compareZoneNames(a.name!, b.name!))
          .map((z) => {
            const isSelected = !hiddenIds.includes(z.id);
            return (
              <LayerRow
                key={z.id}
                item={{ ...z, name: z.name! }}
                isSelected={isSelected}
                isReadOnly={isReadOnly}
                onHoverIdChange={onHoverIdChange}
                visibilityTooltip={visibilityTooltip}
                deleteTooltip={deleteTooltip}
                onToggleSingleHandler={onToggleSingleHandler}
                onDeleteSingleHandler={onDeleteSingleHandler}
              />
            );
          })}
      </Collapse>
    </React.Fragment>
  );
};

const LayerRow: React.FC<{
  item: { id: string; name: string };
  visibilityTooltip: string;
  deleteTooltip: string;
  isSelected: boolean;
  isReadOnly?: boolean | ((id: string) => boolean);
  onHoverIdChange?: (id: string | null) => void;
  onToggleSingleHandler: (id: string) => void;
  onDeleteSingleHandler: (id: string) => void;
}> = ({
  item,
  visibilityTooltip,
  deleteTooltip,
  isSelected,
  isReadOnly = false,
  onHoverIdChange,
  onToggleSingleHandler,
  onDeleteSingleHandler,
}) => {
  const handleOnMouseEnter = () => {
    if (onHoverIdChange != null) {
      onHoverIdChange(item.id);
    }
  };

  const handleOnMouseLeave = () => {
    if (onHoverIdChange != null) {
      onHoverIdChange(null);
    }
  };

  const toggleSingleClickHandler = () => {
    onToggleSingleHandler(item.id);
  };

  const deleteSingleClickHandler = () => {
    onDeleteSingleHandler(item.id);
  };

  return (
    <Stack
      key={item.id}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
      direction="row"
      spacing={1}
      alignItems="center"
      width="100%"
      sx={(theme) => ({ ":hover": { backgroundColor: theme.palette.action.hover } })}
    >
      <Typography variant={"caption"} flexGrow={1} pl={1}>
        {item.name}
      </Typography>
      <Tooltip title={visibilityTooltip} placement="left">
        <Checkbox checked={isSelected} color="primary" onChange={toggleSingleClickHandler} />
      </Tooltip>

      <Tooltip title={deleteTooltip} placement="right">
        <span>
          <ReadOnlyProtectedIconButton color="secondary" onClick={deleteSingleClickHandler} disabled={typeof isReadOnly === "function" ? isReadOnly(item.id) : isReadOnly}>
            <DeleteOutlineIcon />
          </ReadOnlyProtectedIconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default LayersList;
